import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import AppShell from './components/AppShell';
import Panel from './components/Panel';
import TableSkeleton from './components/TableSkeleton';
import { Badge } from './components/StatusPill';
import axiosInstance from './utils/axiosInstance';
import { getAuthToken } from './utils/storageUtil';

interface AdminType {
    id: number;
    username: string;
    createdAt: string;
    isSuperAdmin: boolean;
}

/** Pulls the signed-in admin's id out of the JWT, so we know if they're the super admin. */
function currentAdminId(): number | null {
    try {
        const token = getAuthToken();
        return token ? (JSON.parse(atob(token.split('.')[1])).id as number) : null;
    } catch {
        return null;
    }
}

const AdministratorPage: React.FC = () => {
    const [admins, setAdmins] = useState<AdminType[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [resetAdmin, setResetAdmin] = useState<AdminType | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AdminType | null>(null);
    const [form] = Form.useForm();
    const [resetForm] = Form.useForm();

    const myId = useMemo(currentAdminId, []);
    // Only the super admin sees create/reset/delete; the backend enforces this too.
    const iAmSuper = admins.some((a) => a.id === myId && a.isSuperAdmin);

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('/admin/protected/getAdmins');
            if (response.data && response.data.data) {
                setAdmins(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch admins:', error);
            message.error('加载管理员失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAdmin = async (values: any) => {
        try {
            await axiosInstance.post('/admin/protected/createAdmin', values);
            message.success('管理员创建成功');
            setIsModalOpen(false);
            form.resetFields();
            fetchAdmins();
        } catch (error: any) {
            console.error('Failed to create admin:', error);
            message.error(error.response?.data?.message || '创建管理员失败');
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await axiosInstance.post('/admin/protected/deleteAdmin', { id: deleteTarget.id });
            message.success('管理员已删除');
            setDeleteTarget(null);
            fetchAdmins();
        } catch (error: any) {
            console.error('Failed to delete admin:', error);
            message.error(error.response?.data?.message || '删除管理员失败');
        }
    };

    const handleResetPassword = async (values: any) => {
        if (!resetAdmin) return;
        try {
            await axiosInstance.post('/admin/protected/resetAdminPassword', {
                id: resetAdmin.id,
                newPassword: values.newPassword,
            });
            message.success('密码重置成功');
            setIsResetModalOpen(false);
            resetForm.resetFields();
            setResetAdmin(null);
        } catch (error: any) {
            console.error('Failed to reset password:', error);
            message.error(error.response?.data?.message || '重置密码失败');
        }
    };

    const columns: ColumnsType<AdminType> = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
        {
            title: '用户名',
            dataIndex: 'username',
            key: 'username',
            render: (name: string) => <span className="font-semibold text-ink">{name}</span>,
        },
        {
            title: '角色',
            key: 'role',
            width: 150,
            render: (_, record) =>
                record.isSuperAdmin
                    ? <Badge tone="caution" dot>超级管理员</Badge>
                    : <Badge tone="neutral">管理员</Badge>,
        },
        {
            title: '注册日期',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 200,
            render: (text: string) => (
                <span className="whitespace-nowrap text-muted">
                    {text ? new Date(text).toLocaleString() : '-'}
                </span>
            ),
        },
    ];

    // The action column only exists for the super admin; super-admin rows themselves are protected.
    if (iAmSuper) {
        columns.push({
            title: '操作',
            key: 'action',
            width: 230,
            render: (_, record) =>
                record.isSuperAdmin ? (
                    <span className="text-xs text-faint">受保护</span>
                ) : (
                    <Space size="small">
                        <Button
                            size="small"
                            icon={<KeyOutlined />}
                            onClick={() => {
                                setResetAdmin(record);
                                setIsResetModalOpen(true);
                            }}
                        >
                            重置密码
                        </Button>
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => setDeleteTarget(record)}
                        >
                            删除
                        </Button>
                    </Space>
                ),
        });
    }

    return (
        <AppShell
            title="管理员"
            subtitle={`${admins.length} 个账号`}
            actions={
                iAmSuper ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
                        新建管理员
                    </Button>
                ) : undefined
            }
        >
            <Panel flush>
                {loading ? (
                    <TableSkeleton rows={4} columns={iAmSuper ? 5 : 4} />
                ) : (
                    <Table<AdminType>
                        columns={columns}
                        dataSource={admins}
                        rowKey="id"
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={{ pageSize: 20, hideOnSinglePage: true }}
                        locale={{
                            emptyText: (
                                <div className="py-12 text-center">
                                    <div className="text-[15px] font-medium text-body">没有管理员</div>
                                </div>
                            ),
                        }}
                    />
                )}
            </Panel>

            {!iAmSuper && !loading && (
                <p className="mt-3 text-xs text-faint">
                    只有超级管理员可以新建、删除或重置管理员账号。
                </p>
            )}

            <Modal
                title="新建管理员"
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                okText="创建"
                cancelText="取消"
            >
                <Form form={form} layout="vertical" requiredMark={false} onFinish={handleCreateAdmin} className="mt-4">
                    <Form.Item
                        name="username"
                        label="用户名 (登录账号)"
                        rules={[{ required: true, message: '请输入用户名' }]}
                    >
                        <Input placeholder="输入用户名" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="密码"
                        rules={[{ required: true, message: '请输入密码' }]}
                    >
                        <Input.Password placeholder="输入密码" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`重置密码${resetAdmin ? ` — ${resetAdmin.username}` : ''}`}
                open={isResetModalOpen}
                onCancel={() => {
                    setIsResetModalOpen(false);
                    resetForm.resetFields();
                    setResetAdmin(null);
                }}
                onOk={() => resetForm.submit()}
                okText="重置密码"
                cancelText="取消"
            >
                <Form form={resetForm} layout="vertical" requiredMark={false} onFinish={handleResetPassword} className="mt-4">
                    <Form.Item
                        name="newPassword"
                        label="新密码"
                        rules={[{ required: true, message: '请输入新密码' }]}
                    >
                        <Input.Password placeholder="输入新密码" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="确认删除该管理员？"
                open={!!deleteTarget}
                onOk={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
                okText="删除"
                okType="danger"
                cancelText="取消"
            >
                <p className="text-body">
                    管理员：<span className="font-semibold text-ink">{deleteTarget?.username}</span>
                </p>
                <p className="mt-1 text-xs text-muted">此操作无法撤销。</p>
            </Modal>
        </AppShell>
    );
};

export default AdministratorPage;
